import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

interface PricingRule {
  minQuantity: number;
  discount: number;
}

interface PricingSettings {
  taxRate: number;
  volumeDiscounts: PricingRule[];
  minimumOrderAmount: number;
}

interface PricingContextType {
  pricingSettings: PricingSettings;
  loading: boolean;
  error: string | null;
  refreshPricing: () => Promise<void>;
}

const defaultSettings: PricingSettings = {
  taxRate: 0.08,
  volumeDiscounts: [],
  minimumOrderAmount: 20,
};

const PricingContext = createContext<PricingContextType | undefined>(undefined);

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};

interface PricingProviderProps {
  children: ReactNode;
}

export const PricingProvider: React.FC<PricingProviderProps> = ({ children }) => {
  const [pricingSettings, setPricingSettings] = useState<PricingSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPricingSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await api.settings.getPricing();
      if (settings && settings.volumeDiscounts) {
        setPricingSettings({
          ...defaultSettings,
          ...settings,
          minimumOrderAmount: settings.minimumOrderAmount || defaultSettings.minimumOrderAmount
        });
      } else {
        setPricingSettings(defaultSettings);
      }
    } catch (err) {
      console.error('Failed to load pricing settings:', err);
      setError('Failed to load pricing settings');
      // Use defaults if API fails
      setPricingSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricingSettings();
  }, []);

  const value = {
    pricingSettings,
    loading,
    error,
    refreshPricing: loadPricingSettings,
  };

  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
};